import { Trade } from 'hooks/useRecentTrades'
import { birdeyeFetch } from './helpers'

// --- Birdeye response shapes ---------------------------------------------

interface BirdeyeTradeTokenInfo {
  symbol?: string
  decimals?: number
  address: string
  amount?: number
  uiAmount?: number
  price?: number | null
  nearestPrice?: number | null
  changeAmount?: number
  uiChangeAmount?: number
}

export interface BirdeyeTrade {
  base?: BirdeyeTradeTokenInfo
  quote?: BirdeyeTradeTokenInfo
  basePrice?: number | null
  quotePrice?: number | null
  txHash?: string
  source?: string
  blockUnixTime?: number
  txType?: string
  owner?: string
  side?: 'buy' | 'sell'
  pricePair?: number
  from?: BirdeyeTradeTokenInfo
  to?: BirdeyeTradeTokenInfo
  tokenPrice?: number | null
  poolId?: string
}

interface BirdeyeTradesResponseData {
  items?: BirdeyeTrade[] | null
  hasNext?: boolean
}

// --- REST: defi/txs/token -------------------------------------------------

const BIRDEYE_TRADES_MAX_LIMIT = 50

export async function fetchBirdeyeTokenTrades(args: {
  baseMint: string
  limit?: number
}): Promise<Trade[]> {
  const { baseMint, limit = BIRDEYE_TRADES_MAX_LIMIT } = args
  if (!baseMint) return []

  const params = new URLSearchParams({
    address: baseMint,
    tx_type: 'swap',
    sort_type: 'desc',
    offset: '0',
    limit: String(Math.min(limit, BIRDEYE_TRADES_MAX_LIMIT)),
  })

  const response = await birdeyeFetch<BirdeyeTradesResponseData>(
    `defi/txs/token?${params.toString()}`,
  )
  if (!response.success || !response.data?.items?.length) return []

  // No quote-mint filter: Birdeye returns swaps for the base token across all
  // pairs. Filtering by quote here was throwing away ~90% of results because
  // the API doesn't let us scope to a pair. The venue column already tells the
  // user which pool the swap hit.
  return response.data.items
    .map((t) => birdeyeTradeToTrade(t, baseMint))
    .filter((t): t is Trade => t !== null)
}

// --- Parser: Birdeye trade → app Trade ------------------------------------

// Used for both REST responses and WS TXS_DATA payloads — they share the same
// per-trade shape.
export function birdeyeTradeToTrade(
  t: BirdeyeTrade,
  baseMint: string,
): Trade | null {
  if (!t.txHash || !t.blockUnixTime) return null

  // Birdeye returns trades from the `address` token's perspective. The other
  // leg may be in `from/to` (modern shape) or `base/quote` (legacy shape).
  let basePart: BirdeyeTradeTokenInfo | undefined
  let quotePart: BirdeyeTradeTokenInfo | undefined

  if (t.from?.address === baseMint) {
    basePart = t.from
    quotePart = t.to
  } else if (t.to?.address === baseMint) {
    basePart = t.to
    quotePart = t.from
  } else if (t.base?.address === baseMint) {
    basePart = t.base
    quotePart = t.quote
  } else if (t.quote?.address === baseMint) {
    basePart = t.quote
    quotePart = t.base
  } else {
    return null
  }

  if (!basePart || !quotePart?.address) return null

  const baseSize = Math.abs(basePart.uiChangeAmount ?? basePart.uiAmount ?? 0)
  if (!baseSize) return null

  // Side from base perspective: positive base change = base flowed in = buy.
  const taker_side: 'buy' | 'sell' =
    t.side === 'buy' || t.side === 'sell'
      ? t.side
      : (basePart.uiChangeAmount ?? 0) > 0
        ? 'buy'
        : 'sell'

  // Always store USD/base, not pricePair. Reasoning:
  //   - pricePair direction (quote-per-base vs base-per-quote) isn't documented
  //     consistently across DEX integrations and we've seen inversions.
  //   - REST and WS pull swaps for the base token across all quote pairs, so a
  //     quote-denominated price would be mixed-currency in the same column.
  //     USD/base is unambiguous: every row in the table speaks the same units.
  // Volume math downstream becomes `price * size` = USD, also unambiguous.
  const usdPricePerBase =
    basePart.price ??
    basePart.nearestPrice ??
    t.tokenPrice ??
    null
  if (usdPricePerBase == null || usdPricePerBase <= 0) return null

  return {
    base_token_mint: baseMint,
    quote_token_mint: quotePart.address,
    broker: t.source ?? 'unknown',
    slot: 0, // Birdeye doesn't expose slot; downstream uses signature for dedup
    time: t.blockUnixTime * 1000,
    signature: t.txHash,
    price: usdPricePerBase,
    size: baseSize,
    signer: t.owner ?? '',
    taker_side,
  }
}

// --- WS: SUBSCRIBE_TXS ----------------------------------------------------

export interface BirdeyeTxsDataMessage {
  type: 'TXS_DATA'
  data: BirdeyeTrade
}

export function buildBirdeyeTxsSubscription(baseMint: string): string {
  return JSON.stringify({
    type: 'SUBSCRIBE_TXS',
    data: {
      queryType: 'simple',
      address: baseMint,
      txsType: 'swap',
    },
  })
}

export function buildBirdeyeTxsUnsubscription(): string {
  return JSON.stringify({ type: 'UNSUBSCRIBE_TXS' })
}
